import { useMemo } from "react"
import { eachDayOfInterval, format, parseISO, startOfDay } from "date-fns"
import { Line, LineChart, ResponsiveContainer, Tooltip, XAxis } from "recharts"

interface EntityFeedbackChartProps {
	feedbackDates: string[]
	color: string
	startDate?: Date
	endDate?: Date
}

interface TooltipProps {
	active?: boolean
	payload?: Array<{
		value: number
		dataKey?: string
	}>
	label?: string
}

const CustomTooltip = ({ active, payload, label }: TooltipProps) => {
	if (active && payload && payload.length > 0 && label) {
		const count = payload[0]?.value ?? 0
		const date = parseISO(label)
		const formattedDate = format(date, "MMM d, yyyy")

		return (
			<div className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs shadow-lg dark:border-gray-700 dark:bg-gray-800">
				<p className="font-medium text-gray-900 dark:text-white">
					{formattedDate}
				</p>
				<p className="text-gray-600 dark:text-gray-400">
					{count} {count === 1 ? "request" : "requests"}
				</p>
			</div>
		)
	}
	return null
}

export const EntityFeedbackChart = ({
	feedbackDates,
	color,
	startDate,
	endDate,
}: EntityFeedbackChartProps) => {
	const chartData = useMemo(() => {
		if (feedbackDates.length === 0) {
			return []
		}

		const now = endDate ? startOfDay(endDate) : new Date()
		const rangeStart = startDate
			? startOfDay(startDate)
			: startOfDay(new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000))

		const dateMap = new Map<string, number>()

		const dateRange = eachDayOfInterval({
			start: rangeStart,
			end: now,
		})

		for (const date of dateRange) {
			dateMap.set(format(date, "yyyy-MM-dd"), 0)
		}

		for (const dateStr of feedbackDates) {
			const createdDate = parseISO(dateStr)
			const dateKey = format(startOfDay(createdDate), "yyyy-MM-dd")
			if (dateMap.has(dateKey)) {
				dateMap.set(dateKey, (dateMap.get(dateKey) || 0) + 1)
			}
		}

		return Array.from(dateMap.entries())
			.map(([date, count]) => ({
				date,
				count,
			}))
			.sort((a, b) => a.date.localeCompare(b.date))
	}, [feedbackDates, startDate, endDate])

	if (chartData.length === 0) {
		return null
	}

	const maxCount = Math.max(...chartData.map((d) => d.count))

	if (maxCount === 0) {
		return null
	}

	return (
		<ResponsiveContainer width="100%" height={24}>
			<LineChart
				data={chartData}
				margin={{ top: 2, right: 0, left: 0, bottom: 2 }}
			>
				<XAxis dataKey="date" hide />
				<Tooltip content={<CustomTooltip />} />
				<Line
					type="monotone"
					dataKey="count"
					stroke={color}
					strokeWidth={1.75}
					dot={false}
					isAnimationActive={false}
				/>
			</LineChart>
		</ResponsiveContainer>
	)
}
